import { AppContext } from "@lightspeed-invoicing/core-client/frontend"
import moment from "moment-timezone"
import React, { useEffect, useState } from "react"
import { FormattedMessage, injectIntl } from "react-intl"
import { getEventName } from "../../name-helpers/eventNameHelper"
import { connect, ConnectedProps } from "react-redux"
import CalendarEventEditFormComponent from "./CalendarEventEditFormComponent.jsx"
import FullCalendar from "../fullcalendar.component.jsx"
import CalendarEventDatePickers from "../calendareventdatepickers.component.jsx"
import {
    decreaseCalendarEventStep,
    increaseCalendarEventStep,
    patchCalendarEventForm,
    setCalendarEventStep,
} from "../../state/calendar-event/CalendarEventFormActions"
import {
    getCalendarEventForm,
    getCalendarEventFormStepIndex,
} from "../../state/calendar-event/CalendarEventFormSelectors"
import IntlProps from "../../state/helpers/IntlProps"
import { LocationDto } from "../../state/location/Location"
import { ProjectDto } from "../../state/project/Project"
import Proposal from "../../state/proposal/Proposal"


type OwnProps = {
    calendarEventId?: string,

    invoiceId?: string,
    quoteId?: string,
    orderId?: string,
    projectId?: string,
    project?: ProjectDto
    identifier?: string,

    providedContactId?: string,
    userId?: string,

    location?: LocationDto,
    proposal?: Proposal,

    initialStep?: number,
    lastStep?: number,
    onShowEventForm?: (boolean) => void,
    onSave?: (calendarEventId: string) => void,
    children?: React.ReactNode,
}

const CalendarEventEdit = (props: Props & IntlProps) => {
    const {
        userId = AppContext.getUserLoggedIn().id,
    } = props

    const [calendarEventId, setCalendarEventId] = useState(props.calendarEventId || "")
    let timezone = props.timezone

    useEffect(() => {
        if (calendarEventId) {
            props.setCalendarEventStep(2)
        } else if (props.initialStep) {
            props.setCalendarEventStep(props.initialStep)
        }

        if (props.location && props.location.attributes.timeZone) {
            timezone = props.location.attributes.timeZone
        } else if (AppContext.getUserLoggedIn() && AppContext.getUserLoggedIn().attributes.location) {
            if(AppContext.getUserLoggedIn().attributes.location.attributes.timeZone) {
                timezone = AppContext.getUserLoggedIn().attributes.location.attributes.timeZone
            }
        } else {
            const account = AppContext.getAccount()
            if (account.attributes.timeZone) {
                timezone = account.attributes.timeZone
            }
        }

        if(timezone) {
            moment.tz.setDefault(timezone)
        }
        const now = moment()
        const remainder = 30 - (now.minute() % 30)
        const startAt = now.add(remainder, "m").toDate()

        props.patchCalendarEventForm({
            timezone: timezone,
            startAt: startAt,
            endAt: moment(startAt).add(30, "m").toDate(),
        })

        if (props.providedContactId) {
            props.patchCalendarEventForm({
                contactId: props.providedContactId,
            })
        }
    }, [])

    useEffect(() => {
        switch (props.stepIndex) {
        case 2:
            props.onShowEventForm?.(true)
            break
        default:
            props.onShowEventForm?.(false)
            break
        }
    }, [props.stepIndex])

    let eventTitle = props.project ? props.project.attributes.title : ""
    if (props.identifier) {
        eventTitle = `${props.identifier} - ${eventTitle}`
    }

    const renderStep = () => {
        switch (props.stepIndex) {
        case 1:
        default:
            return (
                <div>
                    <CalendarEventDatePickers
                        startAt={props.startAt}
                        endAt={props.endAt}
                        selectedCalendarId={props.selectedCalendarId}
                        selectedCalendarLabel={props.selectedCalendarLabel}
                        timezone={timezone}
                        onDateChange={(startAt, endAt) => {
                            onStartAtChange(props, props.endAt, startAt)
                            onEndAtChange(props, endAt)
                        }}
                        onCalendarChange={(selectedCalendarId, selectedCalendarLabel, selectedCalendar) => {
                            onCalendarSelectionChange(props, selectedCalendarId, selectedCalendarLabel, selectedCalendar)
                        }}
                    />
                    <FullCalendar
                        key={"full-calendar"}
                        className={"pt-2 pb-3 overflow-auto"}
                        startAt={props.startAt}
                        endAt={props.endAt}
                        calendarId={props.selectedCalendarId}
                        calendarEventId={calendarEventId}
                        timezone={timezone}
                        onTimeSelect={(startAt, endAt) => {
                            props.patchCalendarEventForm({
                                startAt: startAt,
                                endAt: endAt,
                            })
                        }}
                    />
                    {
                        !props.selectedCalendarId &&
                            <div className="w-100 text-center d-flex align-items-center justify-content-center"
                                style={{borderStyle: "dotted", height: "600px"}}>
                                <h5 className="border-0">
                                    <FormattedMessage
                                        id="calendar.new_event.select_calendar_appear_here_label"
                                        defaultMessage="Select a calendar and it will appear here"
                                    />
                                </h5>
                            </div>
                    }
                    {displayEventDatesInformation(props, timezone)}
                    <div className={"d-flex w-100 mt-4"}>
                        <div className="d-flex justify-content-end col-12 pr-0">

                            {
                                (!props.initialStep || props.initialStep < 1) &&
                                    <button
                                        className="btn btn-secondary mr-2"
                                        onClick={() => {
                                            props.decreaseCalendarEventStep()
                                        }}>
                                        <FormattedMessage
                                            id="calendar.new_event.back_btn"
                                            defaultMessage="Back"
                                        />
                                    </button>
                            }
                            {props.children}
                            {
                                !calendarEventId ? <button
                                    className="btn btn-primary"
                                    onClick={() => {
                                        props.increaseCalendarEventStep()
                                    }}
                                    disabled={!props.selectedCalendarId}>
                                    <FormattedMessage
                                        id="calendar.new_event.continue_btn"
                                        defaultMessage="Continue"
                                    />
                                </button> : ""
                            }
                        </div>
                    </div>
                </div>
            )
        case 2:
            return (
                <>
                    <CalendarEventEditFormComponent
                        calendarEventId={calendarEventId}
                        selectedCalendarId={props.selectedCalendarId}
                        selectedCalendarLabel={props.selectedCalendarLabel}
                        startAt={props.startAt}
                        endAt={props.endAt}
                        timezone={timezone}
                        projectId={props.projectId}
                        identifier={props.identifier}
                        proposal={props.proposal}
                        invoiceId={props.invoiceId}
                        quoteId={props.quoteId}
                        orderId={props.orderId}
                        contactId={props.contactId}
                        userId={userId}
                        eventTitle={eventTitle}
                        eventLocation={props.selectedCalendarDefaultLocation}
                        onCalendarChange={(selectedCalendarId, selectedCalendarLabel, selectedCalendar) => {
                            onCalendarSelectionChange(props, selectedCalendarId, selectedCalendarLabel, selectedCalendar)
                        }}
                        onContactChange={(contactId) => {
                            props.patchCalendarEventForm({
                                contactId: contactId,
                            })
                        }}
                        onDateChange={(startAt, endAt) => {
                            onStartAtChange(props, props.endAt, startAt)
                            onEndAtChange(props, endAt)
                        }}
                        onSave={(calendarEventId) => {
                            setCalendarEventId(calendarEventId)
                            if (props.onSave) {
                                props.onSave(calendarEventId)
                            }
                        }}
                        saveButtonLabel={
                            calendarEventId ?
                                <FormattedMessage
                                    id="calendar_event.new_event.update_btn"
                                    defaultMessage="Update {eventName}"
                                    values={{eventName: getEventName(props.intl)}
                                    }
                                /> :
                                <FormattedMessage
                                    id="calendar_event.new_event.create_btn"
                                    defaultMessage="Create {eventName}"
                                    values={{eventName: getEventName(props.intl)}}
                                />
                        }
                        footerModalButton={
                            [
                                !calendarEventId ? <button
                                    className="btn btn-secondary mr-2"
                                    onClick={() => {
                                        props.decreaseCalendarEventStep()
                                    }}>
                                    <FormattedMessage
                                        id="calendar.new_event.back_btn"
                                        defaultMessage="Back"
                                    />
                                </button> : "",
                            ]
                        }
                    />
                </>
            )
        }
    }

    return (
        <>
            {renderStep()}
        </>
    )
}

const onCalendarSelectionChange = (props: Props, selectedCalendarId: string, selectedCalendarLabel: string, selectedCalendar) => {
    props.patchCalendarEventForm({
        selectedCalendarId: selectedCalendarId,
        selectedCalendarLabel: selectedCalendarLabel,
        selectedCalendar: selectedCalendar,
        selectedCalendarDefaultLocation: selectedCalendar ? selectedCalendar.attributes.defaultLocation : "",
    })
}

const onStartAtChange = (props: Props, currentEndAt: Date, newStartAt: Date) => {
    const momentDate = moment(newStartAt)

    props.patchCalendarEventForm({
        startAt: newStartAt,
        endAt: momentDate.isBefore(moment(currentEndAt)) ? currentEndAt : momentDate.add(30, "m").toDate(),
    })
}

const onEndAtChange = (props: Props, newEndAt: Date) => {
    props.patchCalendarEventForm({
        endAt: newEndAt,
    })
}

const displayEventDatesInformation = (props: Props, timezone: string) => {
    if (!props.selectedCalendarId) {
        return ""
    }

    const fullStartDate = moment(props.startAt).format("dddd, LL LT")
    const fullEndDate = moment(props.endAt).format("dddd, LL LT")
    const startDate = moment(props.startAt).format("LL")
    const endDate = moment(props.endAt).format("LL")
    let startTime = moment(props.startAt).format("LT")
    let endTime = moment(props.endAt).format("LT")
    if(timezone) {
        startTime = moment(props.startAt).tz(timezone).format("LT")
        endTime = moment(props.endAt).tz(timezone).format("LT")
    }

    if (startDate === endDate) {
        return <div className="w-100 d-flex justify-content-end">
            <FormattedMessage
                id="calendar.new_event.time_details_same_day"
                defaultMessage="Event on {startDate} from {startTime} to {endTime} ({timezone})"
                values={{
                    startDate: <strong>{startDate}</strong>,
                    startTime: <strong>{startTime}</strong>,
                    endTime: <strong>{endTime}</strong>,
                    timezone: timezone,
                }}
            />
        </div>
    }
    return <div className="w-100 d-flex justify-content-end">
        <FormattedMessage
            id="calendar.new_event.time_details"
            defaultMessage="Event starts on {startAt} and ends on {endAt} ({timezone})"
            values={{
                startAt: <strong>{fullStartDate}</strong>,
                endAt: <strong>{fullEndDate}</strong>,
                timezone: timezone,
            }}
        />
    </div>
}

const mapState = (state, props: OwnProps) => {
    const calendarEventForm = getCalendarEventForm(state)

    return {
        ...props,
        contactId: calendarEventForm.contactId,
        startAt: calendarEventForm.startAt,
        endAt: calendarEventForm.endAt,
        selectedCalendarId: calendarEventForm.selectedCalendarId,
        selectedCalendarLabel: calendarEventForm.selectedCalendarLabel,
        selectedCalendarDefaultLocation: calendarEventForm.selectedCalendarDefaultLocation,
        timezone: calendarEventForm.timezone,
        stepIndex: getCalendarEventFormStepIndex(state),
    }
}
const mapDispatch = {
    patchCalendarEventForm,
    increaseCalendarEventStep,
    decreaseCalendarEventStep,
    setCalendarEventStep,
}

const connector = connect(mapState, mapDispatch)
type Props = ConnectedProps<typeof connector>
export default connector(injectIntl(CalendarEventEdit))


// WEBPACK FOOTER //
// ./src/calendar/components/CalendarEventEditComponent.tsx